import { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, X, Check } from 'lucide-react';

// Typeahead + multi-select combobox — type to narrow a long option list,
// click to toggle a value in/out of the selection. Built for the Supply
// Chain Analytics Dashboard's Location/KAM/Party filters (this codebase had
// no existing combobox/multi-select control to reuse — POTracker.jsx and
// components/FilterBar.jsx only use plain native <select>).
//
// The dropdown panel is rendered through a portal into document.body,
// position: fixed at the trigger button's own screen coordinates, rather
// than position: absolute inside this component's own DOM position. The
// filter bar it lives in scrolls horizontally (overflowX: auto), and per the
// CSS overflow spec that forces the OTHER axis (overflowY) to compute as
// auto too — an absolutely-positioned child panel would get silently
// clipped by that ancestor and never become visible, even with real options
// loaded (this was the "clicking Location/KAM/Party shows no data" bug).
export default function MultiSelectCombo({ label, options, values, onChange, placeholder = 'All', width = 190 }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [rect, setRect] = useState(null);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (rootRef.current && rootRef.current.contains(e.target)) return;
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);

  useEffect(() => {
    if (!open) return;
    const updateRect = () => { if (triggerRef.current) setRect(triggerRef.current.getBoundingClientRect()); };
    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => { window.removeEventListener('scroll', updateRect, true); window.removeEventListener('resize', updateRect); };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [options, query]);

  const toggle = (opt) => {
    if (values.includes(opt)) onChange(values.filter((v) => v !== opt));
    else onChange([...values, opt]);
  };
  const clear = (e) => { e.stopPropagation(); onChange([]); };

  const summary = values.length === 0 ? placeholder : values.length === 1 ? values[0] : `${values.length} selected`;

  const panel = open && rect && createPortal(
    <div
      ref={panelRef}
      style={{
        position: 'fixed', top: rect.bottom + 6, left: rect.left, zIndex: 1000, width: Math.max(rect.width, 220),
        background: '#fff', border: '1px solid #ECE7F2', borderRadius: 12, boxShadow: '0 8px 24px rgba(35,28,48,0.14)',
        overflow: 'hidden', fontFamily: "'Space Grotesk', sans-serif",
      }}
    >
      <div style={{ padding: 8, borderBottom: '1px solid #ECE7F2' }}>
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Search ${label.toLowerCase()}...`}
          style={{
            width: '100%', padding: '7px 9px', borderRadius: 8, border: '1px solid #ECE7F2',
            background: '#FBFAFD', fontFamily: "'Space Grotesk', sans-serif", fontSize: 12.5,
          }}
        />
      </div>
      <div style={{ maxHeight: 220, overflowY: 'auto' }}>
        {filtered.length === 0 && (
          <div style={{ padding: '12px 10px', fontSize: 12, color: '#8C82A0' }}>No matches.</div>
        )}
        {filtered.map((opt) => {
          const checked = values.includes(opt);
          return (
            <div
              key={opt}
              onClick={() => toggle(opt)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', cursor: 'pointer',
                fontSize: 12.5, background: checked ? '#EEE9F8' : 'transparent',
              }}
            >
              <span style={{
                width: 15, height: 15, borderRadius: 4, border: `1px solid ${checked ? '#6E58A6' : '#C9C2D6'}`,
                background: checked ? '#6E58A6' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                {checked && <Check size={11} color="#fff" />}
              </span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opt}</span>
            </div>
          );
        })}
      </div>
      {values.length > 0 && (
        <div style={{ borderTop: '1px solid #ECE7F2', padding: '7px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: '#8C82A0' }}>{values.length} selected</span>
          <button type="button" onClick={() => onChange([])} style={{ border: 'none', background: 'none', color: '#6E58A6', fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}>Clear</button>
        </div>
      )}
    </div>,
    document.body
  );

  return (
    <div ref={rootRef} style={{ position: 'relative', width }}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
          padding: '9px 10px', borderRadius: 10, border: '1px solid #ECE7F2', background: '#FBFAFD',
          color: values.length ? '#231C30' : '#8C82A0', fontFamily: "'Space Grotesk', sans-serif",
          fontSize: 12.5, fontWeight: 600, cursor: 'pointer', textAlign: 'left', whiteSpace: 'nowrap',
          overflow: 'hidden', textOverflow: 'ellipsis',
        }}
        title={values.join(', ')}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{summary}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          {values.length > 0 && (
            <span onClick={clear} title="Clear" style={{ display: 'flex', color: '#9A90AE' }}>
              <X size={13} />
            </span>
          )}
          <ChevronDown size={14} style={{ color: '#8C82A0' }} />
        </span>
      </button>
      {panel}
    </div>
  );
}
